import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  BLOOD_GROUPS,
  createOrUpdateIdCard,
  extractCreatedReference,
  fetchIdCardMaster,
  findOneIdCard,
  formatSapDate,
  getEmployeeDisplayName,
  getLoggedInEmpId,
  getLoggedInUser,
  isSubmitSuccess,
  rememberSubmittedRef,
  uploadIdCardFile,
} from "./idCard.service";

interface MasterOption {
  id?: number;
  type_id: string;
  type_text: string;
  upload_file_level?: string;
}

interface FileDoc {
  uid: string;
  name: string;
  serverKey: string;
  fileExtension: string;
  uploading: boolean;
}

const newUid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const IDCardForm: React.FC = () => {
  const { t } = useT();
  const routes = all_routes;
  const navigate = useNavigate();
  const { id: draftId } = useParams<{ id?: string }>();

  const user = getLoggedInUser();
  const details = user?.details || {};
  const empId = getLoggedInEmpId();
  const fullName = getEmployeeDisplayName(user);
  const designation =
    details?.sap_department_designation_wings?.designation_name ||
    details?.designation_details?.designationName ||
    details?.designation_details?.longText ||
    "";
  const dateOfJoining = details?.date_of_joining ? formatSapDate(details.date_of_joining) : "";
  const dateOfBirth = details?.birthdate ? formatSapDate(details.birthdate) : "";
  const employmentStatus = details?.employmentType || user?.status || "";

  const [contactNumber, setContactNumber] = useState(
    String(details?.phone || details?.mobile || user?.phone || "").replace(/\D/g, "").slice(0, 10)
  );
  const [bloodGroup, setBloodGroup] = useState(String(details?.blood_group || "").toUpperCase());
  const [placeOfPosting, setPlaceOfPosting] = useState("");
  const [idPurpose, setIdPurpose] = useState<MasterOption | null>(null);
  const [srNoInOfficeOrder, setSrNoInOfficeOrder] = useState("");
  const [declaration, setDeclaration] = useState(false);
  const [photoFiles, setPhotoFiles] = useState<FileDoc[]>([]);
  const [signFiles, setSignFiles] = useState<FileDoc[]>([]);
  const [generalFiles, setGeneralFiles] = useState<FileDoc[]>([]);
  const [masterOptions, setMasterOptions] = useState<MasterOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [fetchingDraft, setFetchingDraft] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showDraftSaved, setShowDraftSaved] = useState(false);
  const requestIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    fetchIdCardMaster()
      .then((list) => setMasterOptions(list as MasterOption[]))
      .catch((err) => toast.error(Api.getErrorMessage(err) || "Failed to load purpose options."));
  }, []);

  useEffect(() => {
    if (!draftId) return;
    const loadDraft = async () => {
      setFetchingDraft(true);
      try {
        const d = await findOneIdCard(draftId);
        if (d?.id) requestIdRef.current = Number(d.id);
        setContactNumber(String(d?.ContactNumber || d?.contactNumber || "").replace(/\D/g, "").slice(0, 10));
        setBloodGroup(String(d?.BloodGroup || d?.bloodGroup || "").toUpperCase());
        setPlaceOfPosting(d?.PlaceOfPosting || d?.placeOfPosting || "");
        setSrNoInOfficeOrder(d?.SrNoInTheOffice || d?.srNoInTheOffice || "");
        const reason = d?.ReasonToApply || d?.reasonToApply || "";
        if (reason) setIdPurpose({ type_id: reason, type_text: reason });
        const mapFiles = (arr: any[]): FileDoc[] =>
          (arr || []).map((f: any) => ({
            uid: newUid(),
            name: String(f.FileName || f.fileName || "file").split("/").pop() || "file",
            serverKey: f.FileName || f.fileName || "",
            fileExtension: f.FileExtension || f.fileExtension || "",
            uploading: false,
          }));
        if (d?.ToImage?.results?.length) setPhotoFiles(mapFiles(d.ToImage.results));
        if (d?.ToSign?.results?.length) setSignFiles(mapFiles(d.ToSign.results));
        if (d?.ToDocument?.results?.length) setGeneralFiles(mapFiles(d.ToDocument.results));
      } catch (err: any) {
        toast.error(Api.getErrorMessage(err) || "Failed to load draft.");
      } finally {
        setFetchingDraft(false);
      }
    };
    loadDraft();
  }, [draftId]);

  useEffect(() => {
    if (idPurpose?.type_id && masterOptions.length > 0) {
      const found = masterOptions.find((o) => o.type_id === idPurpose.type_id);
      if (found && found.type_text !== idPurpose.type_text) setIdPurpose(found);
    }
  }, [masterOptions, idPurpose?.type_id]);

  const needsSrNo = idPurpose?.type_id === "V1" || idPurpose?.type_id === "V3";
  const needsGenDoc = !!idPurpose?.upload_file_level;

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "photo" | "sign" | "general"
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (type === "photo") {
      if (photoFiles.length > 0) {
        toast.error(t("only_one_photo_allowed") || "Only one photo is allowed.");
        return;
      }
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (!["jpg", "jpeg"].includes(ext || "") && !["image/jpeg", "image/jpg"].includes(file.type)) {
        toast.error(t("jpeg_only") || "Photo must be JPG/JPEG only.");
        return;
      }
      const kb = file.size / 1024;
      if (kb < 20 || kb > 50) {
        toast.error(t("photo_size_error") || "Passport size photo must be between 20KB to 50KB.");
        return;
      }
    } else if (type === "sign") {
      if (signFiles.length > 0) {
        toast.error(t("only_one_signature_allowed") || "Only one signature is allowed.");
        return;
      }
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (!["jpg", "jpeg"].includes(ext || "") && !["image/jpeg", "image/jpg"].includes(file.type)) {
        toast.error(t("jpeg_only") || "Signature must be JPG/JPEG only.");
        return;
      }
      const kb = file.size / 1024;
      if (kb < 10 || kb > 30) {
        toast.error(t("signature_size_error") || "Signature must be between 10KB to 30KB.");
        return;
      }
    } else {
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
        toast.error(t("document_must_be_pdf") || "Document must be PDF only.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("document_size_error") || "Document must be max 5MB.");
        return;
      }
    }

    const setFn = type === "photo" ? setPhotoFiles : type === "sign" ? setSignFiles : setGeneralFiles;
    const doc: FileDoc = {
      uid: newUid(),
      name: file.name,
      serverKey: "",
      fileExtension: file.type || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/jpeg"),
      uploading: true,
    };
    setFn((prev) => [...prev, doc]);
    try {
      const key = await uploadIdCardFile(file);
      toast.success(`${file.name} uploaded.`);
      setFn((prev) => prev.map((d) => (d.uid === doc.uid ? { ...d, serverKey: key, uploading: false } : d)));
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || `Failed to upload ${file.name}.`);
      setFn((prev) => prev.filter((d) => d.uid !== doc.uid));
    }
  };

  const removeFile = (uid: string, type: "photo" | "sign" | "general") => {
    const setFn = type === "photo" ? setPhotoFiles : type === "sign" ? setSignFiles : setGeneralFiles;
    setFn((prev) => prev.filter((d) => d.uid !== uid));
  };

  const validate = (isDraft: boolean): string[] => {
    if (isDraft) return [];
    const errors: string[] = [];
    if (!empId) errors.push(t("employee_id_not_found") || "Employee ID is required");
    if (!contactNumber.trim()) errors.push(`${t("contact_number") || "Contact number"} ${t("is_required") || "is required"}`);
    else if (!/^\d{10}$/.test(contactNumber)) errors.push(t("invalid_phone_number") || "Enter a valid 10-digit mobile number.");
    if (!bloodGroup) errors.push(`${t("blood_group")} ${t("is_required") || "is required"}`);
    if (!placeOfPosting.trim()) errors.push(`${t("place_of_posting") || t("Place_of_Posting")} ${t("is_required") || "is required"}`);
    if (!idPurpose) errors.push(`${t("Purpose_of_ID")} ${t("is_required") || "is required"}`);
    if (needsSrNo && !srNoInOfficeOrder.trim()) {
      errors.push(`${t("Sr_No_in_Office_Order")} ${t("is_required") || "is required"}`);
    }
    if (!photoFiles.some((f) => f.serverKey && !f.uploading)) errors.push(t("Photo_Required"));
    if (!signFiles.some((f) => f.serverKey && !f.uploading)) errors.push(t("Signature_Required"));
    if (needsGenDoc && !generalFiles.some((f) => f.serverKey && !f.uploading)) {
      errors.push(t("General_Document_Required"));
    }
    if (!declaration) errors.push(t("Declaration"));
    return errors;
  };

  const normalizeExt = (ext: string): string => {
    if (!ext) return "image/jpeg";
    if (ext.startsWith("image/")) return ext;
    if (ext === "application/pdf" || ext === "pdf") return "application/pdf";
    return "image/jpeg";
  };

  const buildPayload = (isDraft: boolean) => {
    const srNo = (srNoInOfficeOrder.trim() || String(empId).slice(-5)).slice(0, 5);
    const makeDocResults = (files: FileDoc[]) =>
      files
        .filter((f) => f.serverKey)
        .map((f) => ({ FileName: f.serverKey, FileExtension: normalizeExt(f.fileExtension) }));
    const payload: any = {
      Action: "C",
      ReasonToApply: idPurpose?.type_id || "",
      BloodGroup: bloodGroup,
      SrNoInTheOffice: srNo,
      ContactNumber: contactNumber,
      PlaceOfPosting: placeOfPosting.trim(),
      EmpID: empId,
      is_draft: isDraft,
      ToImage: { results: makeDocResults(photoFiles) },
      ToSign: { results: makeDocResults(signFiles) },
      ToDocument: { results: makeDocResults(generalFiles) },
    };
    if (requestIdRef.current) payload.id = requestIdRef.current;
    return payload;
  };

  const handleSubmit = async (isDraft: boolean) => {
    const errors = validate(isDraft);
    if (errors.length > 0) {
      setMissingFields(errors);
      setShowMissingModal(true);
      return;
    }
    if (isDraft) setIsSavingDraft(true);
    else setIsSubmitting(true);
    try {
      const res = await createOrUpdateIdCard(buildPayload(isDraft));
      if (isSubmitSuccess(res) || res) {
        if (isDraft) setShowDraftSaved(true);
        else {
          const ref = extractCreatedReference(res);
          if (ref) rememberSubmittedRef(ref);
          setShowThankYou(true);
        }
      }
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || t("something_went_wrong") || "Failed to submit.");
    } finally {
      setIsSavingDraft(false);
      setIsSubmitting(false);
    }
  };

  const renderUpload = (
    label: string,
    hint: string,
    accept: string,
    type: "photo" | "sign" | "general",
    files: FileDoc[],
    inputId: string,
    disabled = false
  ) => (
    <div className="mb-3">
      <label className="form-label fw-semibold">
        {label} <span className="text-danger">*</span>
      </label>
      <p className="text-muted small mb-2">{hint}</p>
      <div className="d-flex gap-2 flex-wrap">
        <input
          type="file"
          id={inputId}
          className="d-none"
          accept={accept}
          onChange={(e) => handleFileChange(e, type)}
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          disabled={disabled || files.some((f) => f.uploading)}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <i className="ti ti-paperclip me-1" />
          {t("choose_file") || "Choose File"}
        </button>
      </div>
      {files.map((f) => (
        <div key={f.uid} className="d-flex align-items-center gap-2 mt-2 p-2 border rounded">
          {f.uploading ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <i className="ti ti-check text-success" />
          )}
          <span className="small flex-grow-1">{f.name}</span>
          {!f.uploading && (
            <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => removeFile(f.uid, type)}>
              <i className="ti ti-trash" />
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const required = <span className="text-danger">*</span>;

  return (
    <div className="page-wrapper">
      <div className="content">
        {fetchingDraft && (
          <div className="text-center py-3">
            <span className="spinner-border spinner-border-sm me-2" />
            {t("loading_draft") || "Loading draft…"}
          </div>
        )}

        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center gap-2">
              <Link to={routes.idCard.list} className="text-decoration-none">
                <i className="ti ti-arrow-left fs-4" />
              </Link>
              <h3 className="page-title mb-0">{t("Application_for_new_I-Card")}</h3>
            </div>
            <nav className="mt-2">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.idCard.list}>{t("see_previous_request")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("form") || "Form"}</li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div
            className="card-header bg-white d-flex justify-content-between align-items-center"
            style={{ cursor: "pointer" }}
            onClick={() => setShowInstructions((v) => !v)}
          >
            <h6 className="mb-0 fw-bold">{t("Following_instructions_for_new_Identity_Card")}</h6>
            <i className={`ti ti-chevron-${showInstructions ? "up" : "down"}`} />
          </div>
          {showInstructions && (
            <div className="card-body py-3 small lh-lg">
              {[1, 2, 3, 4, 5].map((n) => (
                <p key={n} className="mb-2">
                  {n}. {t(`Instruction_${n}`)}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-header bg-white">
            <h5 className="mb-0 fw-bold">{t("Application_for_Issue_of_new_Identity_Card")}</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">{t("name_(full_not_in_abbreviation)") || t("Full_Name")}</label>
                <input className="form-control" value={fullName} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{t("employee_no") || t("Employee_No")}</label>
                <input className="form-control" value={empId} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{t("designation") || t("Designation")}</label>
                <input className="form-control" value={designation} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("date_of_joining") || t("Date_of_Joining")} {required}
                </label>
                <input className="form-control" value={dateOfJoining} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("dob") || "Date of Birth"} {required}
                </label>
                <input className="form-control" value={dateOfBirth} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">{t("employment_status") || t("Employment_Status")}</label>
                <input className="form-control" value={employmentStatus} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("blood_group")} {required}
                </label>
                <select className="form-select" value={bloodGroup} onChange={(e) => setBloodGroup(e.target.value)}>
                  <option value="">{t("Select_Blood_Group")}</option>
                  {BLOOD_GROUPS.map((bg) => (
                    <option key={bg} value={bg}>
                      {bg}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("place_of_posting") || t("Place_of_Posting")} {required}
                </label>
                <input
                  className="form-control"
                  placeholder={t("enter_place_of_posting") || "Enter place of posting"}
                  value={placeOfPosting}
                  onChange={(e) => setPlaceOfPosting(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("contact_no") || t("contact_number")} {required}
                </label>
                <input
                  className="form-control"
                  type="tel"
                  maxLength={10}
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  {t("Application_applied_for_the_reason")} {required}
                </label>
                <select
                  className="form-select"
                  value={idPurpose?.type_id || ""}
                  onChange={(e) => {
                    const opt = masterOptions.find((o) => o.type_id === e.target.value) || null;
                    setIdPurpose(opt);
                    setSrNoInOfficeOrder("");
                    setGeneralFiles([]);
                  }}
                >
                  <option value="">{t("Select_Purpose_of_ID")}</option>
                  {masterOptions.map((opt) => (
                    <option key={opt.type_id} value={opt.type_id}>
                      {opt.type_text}
                    </option>
                  ))}
                </select>
              </div>
              {needsSrNo && (
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    {t("Sr_No_in_Office_Order")} {required}
                  </label>
                  <p className="small text-muted mb-1">{t("numeric_only_note") || "Please enter numeric values only."}</p>
                  <input
                    className="form-control"
                    maxLength={5}
                    value={srNoInOfficeOrder}
                    onChange={(e) => setSrNoInOfficeOrder(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                  />
                </div>
              )}
            </div>

            <hr className="my-4" />

            {renderUpload(
              t("upload_passport_size_studio_quality_photo_") ||
              t("Upload_Passport_Size_Studio_Quality_Latest_Recent_Photo"),
              t("file_size_instruction_photo") || "JPG/JPEG only, 20–50 KB. Studio quality, not a selfie.",
              ".jpg,.jpeg,image/jpeg",
              "photo",
              photoFiles,
              "upload-photo",
              photoFiles.length > 0
            )}
            {renderUpload(
              t("Upload_Sign"),
              t("file_size_instruction") || "JPG/JPEG only, 10–30 KB. Sign on a blank white page.",
              ".jpg,.jpeg,image/jpeg",
              "sign",
              signFiles,
              "upload-sign",
              signFiles.length > 0
            )}
            {needsGenDoc &&
              renderUpload(
                idPurpose?.upload_file_level || t("Upload_File"),
                "PDF only, max 5MB.",
                ".pdf,application/pdf",
                "general",
                generalFiles,
                "upload-general"
              )}

            <div className="alert alert-danger py-2 small mb-0">
              {t("icard_penalty_note") ||
                "An amount of Rs. 100/- will be deducted from the employee's salary in case of I-card loss/damage."}
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="id-card-declaration"
                checked={declaration}
                onChange={(e) => setDeclaration(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="id-card-declaration">
                {t("id_dec") || t("Declaration_Text")}
              </label>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mb-4">
          <Link to={routes.idCard.list} className="btn btn-light">
            {t("cancel") || "Cancel"}
          </Link>
          <button
            type="button"
            className="btn btn-outline-secondary"
            disabled={isSavingDraft || isSubmitting}
            onClick={() => handleSubmit(true)}
          >
            {isSavingDraft ? <span className="spinner-border spinner-border-sm me-1" /> : null}
            {t("save_as_draft")}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={isSubmitting || isSavingDraft}
            onClick={() => handleSubmit(false)}
          >
            {isSubmitting ? <span className="spinner-border spinner-border-sm me-1" /> : null}
            {t("submit") || t("Submit")}
          </button>
        </div>
      </div>

      {showMissingModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">{t("Missing_or_Invalid_Fields")}</h5>
                <button type="button" className="btn-close" onClick={() => setShowMissingModal(false)} />
              </div>
              <div className="modal-body">
                <p>{t("Please_correct_the_following_issues")}</p>
                <ul className="mb-0 text-danger">
                  {missingFields.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-danger" onClick={() => setShowMissingModal(false)}>
                  {t("ok") || "OK"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showThankYou && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center py-4">
                <i className="ti ti-circle-check text-success fs-1 d-block mb-2" />
                <h5>{t("Thank_You_Message") || "Your request has been successfully submitted."}</h5>
                <button className="btn btn-danger mt-3" onClick={() => navigate(routes.idCard.list)}>
                  {t("ok") || "OK"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDraftSaved && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body text-center py-4">
                <i className="ti ti-device-floppy text-primary fs-1 d-block mb-2" />
                <h5>{t("your_application_has_been_saved_as_draft") || "Application saved as draft."}</h5>
                <button className="btn btn-danger mt-3" onClick={() => navigate(routes.idCard.list)}>
                  {t("ok") || "OK"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IDCardForm;
